import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { getAllEmployees, deleteEmployee } from "../utils/methods";
import { strings } from "../utils";
import { Button } from "../components";
import ModalScreen from "../components/modal.jsx";
import DeleteIcon from "../components/Icons/deleteIcon.jsx";
import { CircularProgress } from "@mui/material";
import usePersistData from "../store/usePersistData.js";
import moment from "moment/moment.js";
import { margin, padding } from "@mui/system";

const Employee = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = usePersistData();
  const usersPerPage = 10;

  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get("query") || "";

  useEffect(() => {
    const fetchEmployees = async () => {
      setLoading(true);
      try {
        const employeeData = await getAllEmployees(user.email);
        const sortedUsers = employeeData.sort(
          (a, b) => b.lastUpdated - a.lastUpdated
        );

        const filtered = sortedUsers.filter((employee) => {
          if (
            !employee ||
            !employee.firstName ||
            !employee.email ||
            !employee.designation
          ) {
            return false;
          }

          return (
            employee.firstName
              .toLowerCase()
              .includes(searchQuery.toLowerCase()) ||
            employee.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
            employee.designation
              .toLowerCase()
              .includes(searchQuery.toLowerCase())
          );
        });

        setFilteredUsers(filtered);
      } catch (error) {
        console.error("Error fetching Employees:", error);
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };
    fetchEmployees();
  }, [searchQuery]);

  const handleClickOpen = (employeeId) => {
    setSelectedEmployeeId(employeeId);
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setSelectedEmployeeId(null);
  };

  const handleDeleteEmployee = async () => {
    try {
      if (selectedEmployeeId) {
        await deleteEmployee(selectedEmployeeId);
      }
      handleClose();
    } catch (error) {
      console.error("Error deleting employee:", error);
    }
  };

  const handleAddEmployee = () => navigate(`/employee/edit/new`);
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentEmployees = filteredUsers.slice(
    indexOfFirstUser,
    indexOfLastUser
  );
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  return (
    <div className="container py-8 h-full bg-border overflow-scroll px-[22px]">
      <div className="flex justify-between mb-8">
        <p className="text-[32px] font-semibold">{strings["employeeTitle"]}</p>
        <Button onClick={handleAddEmployee}>{strings["addEmployeeBtn"]}</Button>
      </div>
      <div className="mt-4 flex justify-end">
        <button
          onClick={() => paginate(currentPage - 1)}
          disabled={currentPage === 1}
          className={`px-4 py-2 underline text-black font-bold ${
            currentPage === 1 ? "text-gray-400 cursor-not-allowed" : ""
          }`}
        >
          Previous
        </button>

        <span className="px-4 py-2">{`Page ${currentPage} of ${totalPages}`}</span>
        <button
          onClick={() => paginate(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`px-4 py-2 underline text-black font-bold ${
            currentPage === totalPages ? "text-gray-400 cursor-not-allowed" : ""
          }`}
        >
          Next
        </button>
      </div>

      <TableContainer component={Paper} sx={{ border: "2px solid border" }}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell align="center">Name</TableCell>
              <TableCell align="center">Email</TableCell>
              <TableCell align="center">CNIC</TableCell>
              <TableCell align="center">Designation</TableCell>
              <TableCell align="center">Joining Date</TableCell>
              <TableCell align="center">Leaves</TableCell>
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : currentEmployees.length > 0 ? (
              currentEmployees.map((employee, index) => (
                <TableRow
                  key={index}
                  onClick={() => navigate(`/leaveRequest/${employee.id}`)}
                  style={{ cursor: "pointer" }}
                >
                  <TableCell align="center">
                    {employee.firstName || "N/A"}
                  </TableCell>
                  <TableCell align="center">
                    {employee.email || "N/A"}
                  </TableCell>
                  <TableCell align="center">{employee.cnic || "N/A"}</TableCell>
                  <TableCell align="center">
                    {employee.designation || "N/A"}
                  </TableCell>
                  <TableCell align="center">
                    {moment(employee.joiningDate).format("MMM DD, YYYY") ||
                      "N/A"}
                  </TableCell>

                  <TableCell align="center">
                    {employee.leaveBalance?.totalLeaves || "N/A"}
                  </TableCell>

                  <TableCell align="center">
                    <div
                      className={`flex items-center gap-x-2 ${
                        employee.designation !== "ADMIN"
                          ? "justify-center"
                          : "justify-start ml-3"
                      }`}
                    >
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/employee/edit/${employee.id}`);
                        }}
                      >
                        {strings["blogEditBtn"]}
                      </Button>
                      {employee.designation !== "ADMIN" && (
                        <Button
                          variant="outlineArrow"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClickOpen(employee.id);
                          }}
                        >
                          {strings["DeleteBtn"]}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  No record found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <div className="mt-4 flex justify-end">
        <button
          onClick={() => paginate(currentPage - 1)}
          disabled={currentPage === 1}
          className={`px-4 py-2 underline text-black font-bold ${
            currentPage === 1 ? "text-gray-400 cursor-not-allowed" : ""
          }`}
        >
          Previous
        </button>

        <span className="px-4 py-2">{`Page ${currentPage} of ${totalPages}`}</span>
        <button
          onClick={() => paginate(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`px-4 py-2 underline text-black font-bold ${
            currentPage === totalPages ? "text-gray-400 cursor-not-allowed" : ""
          }`}
        >
          Next
        </button>
      </div>

      <ModalScreen
        open={openDialog}
        onClose={handleClose}
        onConfirm={handleDeleteEmployee}
        title={strings["deleteEmployeeTitle"]}
        description={strings["deleteEmployeeDesc"]}
        confirmButtonText={strings["DeleteBtn"]}
        cancelButtonText={strings["cancelBtn"]}
        hideClass="hidden"
        icon={DeleteIcon}
      />
    </div>
  );
};

export default Employee;
